import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  FaArrowLeft,
  FaBolt,
  FaCube,
  FaFileInvoice,
  FaCalendarAlt,
  FaBatteryHalf,
  FaChartBar,
  FaMapMarkerAlt,
  FaHardHat,
  FaMap,
  FaPrint,
  FaDownload,
  FaCircle,
  FaHistory,
  FaExclamationTriangle,
  FaThermometerHalf,
  FaSyncAlt,
} from 'react-icons/fa';
import { Chart as ChartJS, CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend } from 'chart.js';
import { Line } from 'react-chartjs-2';
import {
  getStatusLabel,
  getBatteryModeLabel,
  getHealthColor,
  SMPS_COLORS,
} from '../utils/smpsConstants';
import { fetchDeviceHistory, DeviceHistoryResponse } from '../utils/smpsApiService';
import '../smps-dashboard.scss';

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend);

type DetailTab = 'overview' | 'power' | 'location' | 'performance';

const SMPSDeviceDetail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const id = searchParams.get('deviceId');
  const name = searchParams.get('deviceName');
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<DetailTab>('overview');

  // API data state
  const [historyData, setHistoryData] = useState<DeviceHistoryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Load data function using conventional axios method
  const loadData = useCallback(async (showLoading = true) => {
    if (!id || !name) return;

    if (showLoading) {
      setIsLoading(true);
    } else {
      setIsRefreshing(true);
    }
    setIsError(false);

    try {
      const data = await fetchDeviceHistory({ deviceId: id, size: 20 });
      setHistoryData(data);
    } catch (err) {
      console.error('Error loading device history:', err);
      setIsError(true);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [id]);

  // Initial data load
  useEffect(() => {
    loadData();
  }, [loadData]);
  console.log(historyData, "data");

  // Auto-refresh every 60 seconds
  useEffect(() => {
    pollingIntervalRef.current = setInterval(() => {
      if (!isLoading && !isRefreshing) {
        loadData(false);
      }
    }, 60000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [loadData, isLoading, isRefreshing]);

  // Refetch function for manual refresh
  const refetch = useCallback(() => {
    loadData(false);
  }, [loadData]);

  const deviceData = historyData?.device || null;
  const voltageHistory = historyData?.voltageHistory || [];

  // Performance chart data from API history
  const performanceChartData = useMemo(() => {
    // Use historical data if available, otherwise show current value
    const historicalData = voltageHistory.slice(0, 7).reverse();
    const labels = historicalData.length > 0
      ? historicalData.map((h) => h.timestamp.split(' ')[1] || 'N/A')
      : ['Now'];
    const voltageData = historicalData.length > 0
      ? historicalData.map((h) => h.dcVoltage)
      : [deviceData?.dcVoltage || 0];

    return {
      labels,
      datasets: [
        {
          label: 'DC Voltage (V)',
          data: voltageData,
          borderColor: SMPS_COLORS.primary,
          backgroundColor: 'rgba(26, 82, 118, 0.1)',
          fill: true,
          tension: 0.4,
        },
      ],
    };
  }, [deviceData, voltageHistory]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
    },
    scales: {
      y: { beginAtZero: false, min: 42, max: 56 },
    },
  };

  const handlePrint = () => window.print();

  const handleExport = () => {
    alert('PDF export functionality would be implemented here');
  };

  const tabs: { id: DetailTab; label: string; icon: React.ReactNode }[] = [
    { id: 'overview', label: 'Overview', icon: <FaCube /> },
    { id: 'power', label: 'Power', icon: <FaBolt /> },
    { id: 'location', label: 'Location', icon: <FaMapMarkerAlt /> },
    { id: 'performance', label: 'Performance', icon: <FaChartBar /> },
  ];

  // Loading state
  if (isLoading) {
    return (
      <div className="smps-dashboard">
        <div className="detail-page">
          <div className="no-data">
            <FaSyncAlt className="no-data-icon spin" />
            <h3>Loading device details...</h3>
          </div>
        </div>
      </div>
    );
  }

  // Error or not found state
  if (isError || !deviceData) {
    return (
      <div className="smps-dashboard">
        <div className="detail-page">
          <div className="no-data">
            <FaExclamationTriangle className="no-data-icon" />
            <h3>{isError ? 'Failed to load device details' : 'Device not found'}</h3>
            <p style={{ color: '#64748b', marginBottom: '20px' }}>
              {isError ? 'Please check your connection and try again' : 'The requested device could not be found'}
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              {isError && (
                <button className="view-btn" onClick={() => refetch()}>
                  <FaSyncAlt /> Retry
                </button>
              )}
              <button className="back-btn" onClick={() => navigate('/smps-dashboard')}>
                <FaArrowLeft /> Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { assetDetails, battery, acInput, rectifiers, temperature, alarms } = deviceData;

  return (
    <div className="smps-dashboard">
      <div className="detail-page">
        {/* Header */}
        <div className="detail-header">
          <div className="header-left">
            <button className="back-btn" onClick={() => navigate('/smps-dashboard')}>
              <FaArrowLeft />
            </button>
            <div className="header-info">
              <h1>
                <FaBolt />
                {deviceData.name}
              </h1>
              <div className="header-meta">
                <span className="station-badge">{deviceData.station}</span>
                <span
                  className="health-badge"
                  style={{ backgroundColor: getHealthColor(deviceData.status) }}
                >
                  {getStatusLabel(deviceData.status)}
                </span>
                <span className="ip-badge">{assetDetails.ipAddress}</span>
              </div>
            </div>
          </div>
          <div className="header-actions">
            <button
              className="view-btn"
              onClick={() => refetch()}
              disabled={isRefreshing}
              style={{ opacity: isRefreshing ? 0.7 : 1 }}
            >
              <FaSyncAlt className={isRefreshing ? 'spin' : ''} /> {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </button>
            <button className="view-btn" onClick={() => navigate(`/smps-device-history/${id}`)}>
              <FaHistory /> View History
            </button>
            <button className="view-btn" onClick={handlePrint}>
              <FaPrint /> Print
            </button>
            <button className="view-btn primary" onClick={handleExport}>
              <FaDownload /> Export PDF
            </button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="detail-stats">
          <div className="detail-stat-card">
            <FaBolt />
            <div className="stat-content">
              <div className="stat-value">{deviceData.dcVoltage > 0 ? `${deviceData.dcVoltage}V` : '-'}</div>
              <div className="stat-label">DC Voltage</div>
            </div>
          </div>
          <div className="detail-stat-card">
            <FaBolt />
            <div className="stat-content">
              <div className="stat-value">{deviceData.loadCurrent > 0 ? `${deviceData.loadCurrent}A` : '-'}</div>
              <div className="stat-label">Load Current</div>
            </div>
          </div>
          <div className="detail-stat-card">
            <FaBatteryHalf />
            <div className="stat-content">
              <div className="stat-value">{battery.capacity > 0 ? `${battery.capacity}%` : '-'}</div>
              <div className="stat-label">Battery</div>
            </div>
          </div>
          <div className="detail-stat-card">
            <FaThermometerHalf />
            <div className="stat-content">
              <div className="stat-value">{temperature.ambient > 0 ? `${temperature.ambient}°C` : '-'}</div>
              <div className="stat-label">Temperature</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="detail-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`detail-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="detail-content">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="info-grid">
              <div className="info-section">
                <h4><FaCube /> Basic Information</h4>
                <div className="info-item">
                  <div className="info-label">Make:</div>
                  <div className="info-value">{assetDetails.make}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Model:</div>
                  <div className="info-value">{assetDetails.model}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Serial Number:</div>
                  <div className="info-value highlight">{assetDetails.serialNumber}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Capacity:</div>
                  <div className="info-value">{assetDetails.capacity}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Firmware:</div>
                  <div className="info-value">{assetDetails.firmwareVersion}</div>
                </div>
              </div>

              <div className="info-section">
                <h4><FaFileInvoice /> Purchase & Warranty</h4>
                <div className="info-item">
                  <div className="info-label">Purchase Date:</div>
                  <div className="info-value">{assetDetails.purchaseDate}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Installation Date:</div>
                  <div className="info-value">{assetDetails.installationDate}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Warranty Expiry:</div>
                  <div className="info-value">{assetDetails.warrantyExpiry}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Warranty Status:</div>
                  <div className={`info-value ${assetDetails.warrantyStatus === 'Active' ? 'success' : 'danger'}`}>
                    {assetDetails.warrantyStatus}
                  </div>
                </div>
              </div>

              <div className="info-section">
                <h4><FaCalendarAlt /> Lifecycle</h4>
                <div className="info-item">
                  <div className="info-label">Codal Life:</div>
                  <div className="info-value">{assetDetails.codalLife}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Remaining Life:</div>
                  <div className="info-value success">{assetDetails.remainingLife}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Installation Age:</div>
                  <div className="info-value">{assetDetails.installationAge}</div>
                </div>
              </div>
            </div>
          )}

          {/* Power Tab */}
          {activeTab === 'power' && (
            <div className="info-grid">
              <div className="info-section">
                <h4><FaBolt /> DC Output</h4>
                <div className="info-item">
                  <div className="info-label">DC Voltage:</div>
                  <div className="info-value">{deviceData.dcVoltage > 0 ? `${deviceData.dcVoltage}V` : '-'}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Load Current:</div>
                  <div className="info-value">{deviceData.loadCurrent > 0 ? `${deviceData.loadCurrent}A` : '-'}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Efficiency:</div>
                  <div className="info-value">{deviceData.efficiency > 0 ? `${deviceData.efficiency}%` : '-'}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Float Voltage:</div>
                  <div className="info-value">{deviceData.floatVoltage > 0 ? `${deviceData.floatVoltage}V` : '-'}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Boost Voltage:</div>
                  <div className="info-value">{deviceData.boostVoltage > 0 ? `${deviceData.boostVoltage}V` : '-'}</div>
                </div>
              </div>

              <div className="info-section">
                <h4><FaBatteryHalf /> Battery</h4>
                <div className="info-item">
                  <div className="info-label">Voltage:</div>
                  <div className="info-value">{battery.voltage > 0 ? `${battery.voltage}V` : '-'}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Current:</div>
                  <div className="info-value">{battery.current !== 0 ? `${battery.current}A` : '-'}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">SOC:</div>
                  <div className={`info-value ${battery.capacity > 50 ? 'success' : 'danger'}`}>{battery.capacity > 0 ? `${battery.capacity}%` : '-'}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Mode:</div>
                  <div className={`info-value ${battery.mode === 'FLOAT' ? 'success' : 'warning'}`}>{getBatteryModeLabel(battery.mode)}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Capacity:</div>
                  <div className="info-value">{battery.capacityAh > 0 ? `${battery.capacityAh} Ah` : '-'}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Banks/BMS:</div>
                  <div className="info-value">{battery.count}/{battery.bmsCount}</div>
                </div>
              </div>

              <div className="info-section">
                <h4><FaBolt /> AC Input ({acInput.inputType})</h4>
                <div className="status-indicators">
                  <div className={`status-indicator-item ${acInput.phaseR > 0 ? 'up' : 'down'}`}>
                    <FaCircle />
                    <span>Phase R: {acInput.phaseR > 0 ? `${acInput.phaseR}V` : 'FAIL'}</span>
                  </div>
                  <div className={`status-indicator-item ${acInput.phaseY > 0 ? 'up' : 'down'}`}>
                    <FaCircle />
                    <span>Phase Y: {acInput.phaseY > 0 ? `${acInput.phaseY}V` : 'FAIL'}</span>
                  </div>
                  <div className={`status-indicator-item ${acInput.phaseB > 0 ? 'up' : 'down'}`}>
                    <FaCircle />
                    <span>Phase B: {acInput.phaseB > 0 ? `${acInput.phaseB}V` : 'FAIL'}</span>
                  </div>
                </div>
                <div className="info-item" style={{ marginTop: '10px' }}>
                  <div className="info-label">Frequency:</div>
                  <div className="info-value">{acInput.frequency > 0 ? `${acInput.frequency} Hz` : '-'}</div>
                </div>
              </div>

              <div className="info-section">
                <h4><FaChartBar /> Rectifiers</h4>
                <div className="info-item">
                  <div className="info-label">Online:</div>
                  <div className={`info-value ${rectifiers.online === rectifiers.total ? 'success' : 'warning'}`}>{rectifiers.online}/{rectifiers.total}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Total Current:</div>
                  <div className="info-value">{rectifiers.totalCurrent > 0 ? `${rectifiers.totalCurrent}A` : '-'}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Total Power:</div>
                  <div className="info-value">{rectifiers.totalPower > 0 ? `${(rectifiers.totalPower / 1000).toFixed(2)} kW` : '-'}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Current Limit:</div>
                  <div className="info-value">{rectifiers.currentLimit > 0 ? `${rectifiers.currentLimit}A` : '-'}</div>
                </div>
              </div>

              {deviceData.solar.slots > 0 && (
                <div className="info-section">
                  <h4><FaBolt /> Solar</h4>
                  <div className="info-item">
                    <div className="info-label">Slots:</div>
                    <div className="info-value">{deviceData.solar.slots}</div>
                  </div>
                  <div className="info-item">
                    <div className="info-label">Power:</div>
                    <div className="info-value success">{deviceData.solar.totalPower > 0 ? `${(deviceData.solar.totalPower / 1000).toFixed(2)} kW` : '-'}</div>
                  </div>
                </div>
              )}

              {deviceData.loads.some(l => l.current > 0 || l.todayEnergy > 0) && (
                <div className="info-section">
                  <h4><FaBolt /> Load Outputs</h4>
                  {deviceData.loads.map((load, idx) => (
                    <div key={idx} className="info-item">
                      <div className="info-label">Load {idx + 1}:</div>
                      <div className="info-value">
                        {load.current > 0 ? `${load.current.toFixed(2)}A` : '0A'}
                        {load.todayEnergy > 0 ? ` (${(load.todayEnergy / 1000).toFixed(1)} kWh today)` : ''}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Location Tab */}
          {activeTab === 'location' && (
            <div className="info-grid">
              <div className="info-section">
                <h4><FaMapMarkerAlt /> Location</h4>
                <div className="info-item">
                  <div className="info-label">Station:</div>
                  <div className="info-value highlight">{deviceData.station}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Latitude:</div>
                  <div className="info-value">{assetDetails.latitude}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Longitude:</div>
                  <div className="info-value">{assetDetails.longitude}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Position:</div>
                  <div className="info-value">{assetDetails.assetPosition}</div>
                </div>
              </div>

              <div className="info-section">
                <h4><FaHardHat /> Installation</h4>
                <div className="info-item">
                  <div className="info-label">Team:</div>
                  <div className="info-value">{assetDetails.installationTeam}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Supervisor:</div>
                  <div className="info-value">{assetDetails.supervisor}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Last Maintenance:</div>
                  <div className="info-value">{assetDetails.lastMaintenance}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Next Due:</div>
                  <div className="info-value warning">{assetDetails.nextMaintenanceDue}</div>
                </div>
              </div>

              <div className="info-section full-width">
                <h4><FaMap /> Location Map</h4>
                <div className="map-container">
                  <div className="map-placeholder">
                    <FaMapMarkerAlt className="map-icon" />
                    <p>Location: {deviceData.station} Station</p>
                    <p>Coordinates: {assetDetails.latitude}, {assetDetails.longitude}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Performance Tab */}
          {activeTab === 'performance' && (
            <div className="performance-section">
              <div className="chart-card full-width">
                <div className="chart-title">
                  <h3><FaChartBar /> DC Voltage Trend</h3>
                </div>
                <div className="chart-container" style={{ height: '300px' }}>
                  <Line data={performanceChartData} options={chartOptions} />
                </div>
              </div>

              <div className="info-grid">
                <div className="info-section">
                  <h4><FaThermometerHalf /> Battery Temperatures</h4>
                  <div className="info-item">
                    <div className="info-label">Sensor 1:</div>
                    <div className={`info-value ${temperature.battery > 35 ? 'warning' : 'success'}`}>{temperature.battery > 0 ? `${temperature.battery}°C` : '-'}</div>
                  </div>
                  <div className="info-item">
                    <div className="info-label">Sensor 2:</div>
                    <div className={`info-value ${temperature.battery2 > 35 ? 'warning' : 'success'}`}>{temperature.battery2 > 0 ? `${temperature.battery2}°C` : '-'}</div>
                  </div>
                  <div className="info-item">
                    <div className="info-label">Sensor 3:</div>
                    <div className={`info-value ${temperature.battery3 > 35 ? 'warning' : 'success'}`}>{temperature.battery3 > 0 ? `${temperature.battery3}°C` : '-'}</div>
                  </div>
                  <div className="info-item">
                    <div className="info-label">Sensor 4:</div>
                    <div className={`info-value ${temperature.battery4 > 35 ? 'warning' : 'success'}`}>{temperature.battery4 > 0 ? `${temperature.battery4}°C` : '-'}</div>
                  </div>
                </div>

                <div className="info-section">
                  <h4><FaThermometerHalf /> Environment</h4>
                  <div className="info-item">
                    <div className="info-label">Ambient:</div>
                    <div className={`info-value ${temperature.ambient > 40 ? 'warning' : 'success'}`}>{temperature.ambient > 0 ? `${temperature.ambient}°C` : '-'}</div>
                  </div>
                  <div className="info-item">
                    <div className="info-label">Ext. Temp 1:</div>
                    <div className="info-value">{deviceData.environment.temperature1 > 0 ? `${deviceData.environment.temperature1.toFixed(1)}°C` : '-'}</div>
                  </div>
                  <div className="info-item">
                    <div className="info-label">Ext. Temp 2:</div>
                    <div className="info-value">{deviceData.environment.temperature2 > 0 ? `${deviceData.environment.temperature2.toFixed(1)}°C` : '-'}</div>
                  </div>
                  <div className="info-item">
                    <div className="info-label">Humidity:</div>
                    <div className="info-value">{deviceData.environment.humidity > 0 ? `${deviceData.environment.humidity.toFixed(1)}%` : '-'}</div>
                  </div>
                </div>

                <div className="info-section">
                  <h4><FaExclamationTriangle /> Alarms & Status</h4>
                  <div className="info-item">
                    <div className="info-label">Active Alarms:</div>
                    <div className={`info-value ${alarms.activeCount > 0 ? 'danger' : 'success'}`}>{alarms.activeCount}</div>
                  </div>
                  <div className="info-item">
                    <div className="info-label">LVD Status:</div>
                    <div className={`info-value ${alarms.lvdStatus ? 'danger' : 'success'}`}>{alarms.lvdStatus ? 'ACTIVE' : 'OK'}</div>
                  </div>
                  <div className="info-item">
                    <div className="info-label">Over Voltage:</div>
                    <div className={`info-value ${alarms.overVoltage ? 'danger' : 'success'}`}>{alarms.overVoltage ? 'ALARM' : 'OK'}</div>
                  </div>
                  <div className="info-item">
                    <div className="info-label">Under Voltage:</div>
                    <div className={`info-value ${alarms.underVoltage ? 'danger' : 'success'}`}>{alarms.underVoltage ? 'ALARM' : 'OK'}</div>
                  </div>
                  <div className="info-item">
                    <div className="info-label">Buzzer:</div>
                    <div className={`info-value ${alarms.buzzerEnabled ? 'warning' : 'success'}`}>{alarms.buzzerEnabled ? 'ON' : 'OFF'}</div>
                  </div>
                </div>

                <div className="info-section">
                  <h4><FaChartBar /> System Info</h4>
                  <div className="info-item">
                    <div className="info-label">Site Name:</div>
                    <div className="info-value">{deviceData.systemInfo.siteName || '-'}</div>
                  </div>
                  <div className="info-item">
                    <div className="info-label">System Time:</div>
                    <div className="info-value">{deviceData.systemInfo.systemTime || '-'}</div>
                  </div>
                  <div className="info-item">
                    <div className="info-label">Power Mode:</div>
                    <div className="info-value">{deviceData.systemInfo.powerMode || '-'}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SMPSDeviceDetail;
